import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAddCaseFormContext } from '../contexts/AddCaseFormContext';
import useMedicalFileUpload from './useMedicalFileUpload';
import useCaseFieldUpdate from './useCaseFieldUpdate';
import useCaseRecordDelete from './useCaseRecordDelete';
import { buildMedicalProviderPaths } from '../utils/caseFieldPaths';
import { validationFilesFunc } from '../utils/helper';
import { splitResolvedProviders, resolveBucketTarget } from '../utils/medicalProviderHelpers';
import { UPDATE_CASE_ACTION, UNASSIGNED_PROVIDER_NAME } from '@demandproai/shared';

/**
 * Drives the AI-mode single drop-zone for a timing section. Files land in a
 * single placeholder (__UNASSIGNED__) entry the dedup pipeline later replaces
 * with real providers. Once resolved providers exist, uploads update only the
 * placeholder entry and leave the resolved siblings intact. No provider-name input.
 * @param {Object} params
 * @param {MEDICAL_PROVIDER_TYPE[keyof MEDICAL_PROVIDER_TYPE]} params.providerType - timing section
 * @returns {{ isLoading: boolean, addBucketFiles: Function, addBucketFilesFromArray: Function, removeBucketFile: Function, bucketFiles: Array, providerType: string }}
 */
const useAllProvidersBucket = ({ providerType }) => {
  const { formik, selectedTiffIndex, setFileErrorList } = useAddCaseFormContext();
  const { caseId } = useParams();
  const { handleFileRemove } = useCaseRecordDelete();

  const formikValues = formik.values;
  const setFieldValue = formik.setFieldValue;

  const { uploadMedicalRecords, isLoading } = useMedicalFileUpload({ providerType });
  const { updateCase } = useCaseFieldUpdate({ caseId });

  const paths = buildMedicalProviderPaths({ selectedTiffIndex, providerType });
  const recordsPath = `clients[${selectedTiffIndex}].medicalProviders.${providerType}.medicalRecords`;

  const currentRecords = formikValues?.clients?.[selectedTiffIndex]?.medicalProviders?.[providerType]?.medicalRecords;
  const { unassigned } = splitResolvedProviders(currentRecords);
  const bucketFiles = unassigned?.source ?? [];
  const bucketIndex = (Array.isArray(currentRecords) ? currentRecords : [])
    .findIndex((record) => record?.providerName === UNASSIGNED_PROVIDER_NAME);

  const uploadBucketFiles = useCallback(async (files) => {
    const validFiles = await validationFilesFunc(files, setFileErrorList);
    const currentClient =  formikValues?.clients?.[selectedTiffIndex];
    const existingRecords = currentClient?.medicalProviders?.[providerType]?.medicalRecords || [];
    const { exists, index: targetIndex, source: existingSource } = resolveBucketTarget(existingRecords);

    if (!exists) {
      await updateCase({ data: { [paths.records]: { providerName: UNASSIGNED_PROVIDER_NAME, source: [] } }, action: UPDATE_CASE_ACTION.ADD });
    }

    const buildRecords = (bucketSource) => {
      const next = [...existingRecords];
      next[targetIndex] = { providerName: UNASSIGNED_PROVIDER_NAME, source: bucketSource };
      return next;
    };

    setFieldValue(recordsPath, buildRecords([...existingSource, ...validFiles]));

    const dedicatedFiles = await uploadMedicalRecords(validFiles, {
      fieldKey: paths.source(targetIndex),
      action: 'add'
    });

    setFieldValue(recordsPath, buildRecords([...existingSource, ...dedicatedFiles]));
  }, [formikValues, selectedTiffIndex, providerType, setFieldValue, setFileErrorList, updateCase, uploadMedicalRecords, paths, recordsPath]);

  const addBucketFiles = async (e) => {
    const files = Array.from(e.currentTarget.files);
    await uploadBucketFiles(files);
  };

  const removeBucketFile = (fileToRemove) => {
    handleFileRemove({
      formik,
      fileToRemove,
      filePath: `${recordsPath}[${bucketIndex}].source`,
      removeData: { [paths.source(bucketIndex)]: { s3UrlPath: fileToRemove.s3UrlPath } }
    });
  };

  return {
    isLoading,
    addBucketFiles,
    addBucketFilesFromArray: uploadBucketFiles,
    removeBucketFile,
    bucketFiles,
    providerType
  };
};

export default useAllProvidersBucket;
